// ============================================
// FILE: src/features/lockers/components/LockerCreate.tsx
// ============================================

import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  required,
  useTranslate,
  useNotify,
  useRedirect,
  useGetIdentity,
  CreateProps,
  FormDataConsumer,
} from 'react-admin';
import {
  Box,
  Alert,
  Grid,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

// COMPONENTES DEL SISTEMA
import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import {
  CenteredFormLayout,
  MyFormSection,
} from '../../../components/forms/MyFormLayout';

// Hooks y tipos
import { useLockerPermissions } from '../hooks/useLockerPermissions';
import { useLockerValidation } from '../hooks/useLockerValidation';
import { prepareLockerForAPI, LockerFormData } from '../types';

// Validaciones locales
const validateDisplayNumber = (value: string) => {
  if (!value || value.trim().length === 0) {
    return 'El número de taquilla es requerido';
  }
  if (value.length > 10) {
    return 'El número no puede exceder 10 caracteres';
  }
  return undefined;
};

const validateHardwareId = (value: number) => {
  if (!value || value < 1) {
    return 'El ID de hardware debe ser mayor a 0';
  }
  return undefined;
};

const LockerCreate: React.FC<CreateProps> = (props) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const { data: identity } = useGetIdentity();
  const { canCreate } = useLockerPermissions();

  // Check permissions
  if (!canCreate) {
    return (
      <MyFormContainer maxWidth="md">
        <Alert severity="error">
          {translate('resources.lockers.messages.no_permission_create')}
        </Alert>
      </MyFormContainer>
    );
  }

  const transform = (data: LockerFormData) => {
      const preparedData = {
      displayNumber: data.displayNumber?.trim(),
      hardwareId: data.hardwareId, // Mantener como número si el backend lo espera así
      hotelId: data.hotelId,
      zoneId: data.zoneId,
      lockerTypeId: data.lockerTypeId,
      lockerControllerId: data.lockerControllerId || null,
      notes: data.notes?.trim() || null,
    };
    return preparedData;
  };

  const handleSuccess = (data: any) => {
    notify(translate('resources.lockers.messages.create_success'), { type: 'success' });
    redirect('list', 'lockers');
  };

  const handleCancel = () => redirect('list', 'lockers');

  return (
    <Create
      {...props}
      title={translate('resources.lockers.titles.create')}
      transform={transform}
      mutationOptions={{ onSuccess: handleSuccess }}
      redirect={false}
    >
      <MyFormContainer>
        <SimpleForm toolbar={<MyToolbar onCancel={handleCancel} />}>
          <CenteredFormLayout>
            <MyFormSection
              title={translate('resources.lockers.sections.create', 'Nueva Taquilla')}
              subtitle={translate('resources.lockers.sections.create_subtitle', 'Datos básicos de la taquilla')}
              icon={<LockIcon />}
              variant="outlined"
            >
              {/* Display Number y Hardware ID en una línea */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="displayNumber"
                    label={translate('resources.lockers.fields.displayNumber')}
                    validate={[required(), validateDisplayNumber]}
                    fullWidth
                    size="small"
                    placeholder="001"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <NumberInput
                    source="hardwareId"
                    label={translate('resources.lockers.fields.hardwareId')}
                    validate={[required(), validateHardwareId]}
                    fullWidth
                    size="small"
                    min={1}
                  />
                </Grid>
              </Grid>

              {/* Hotel y Zona */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ReferenceInput
                    source="hotelId"
                    reference="hotels"
                    label={translate('resources.lockers.fields.hotel')}
                    sort={{ field: 'name', order: 'ASC' }}
                    filter={identity?.role !== 'SuperAdmin' ? { id: identity?.hotelId } : {}}
                  >
                    <SelectInput
                      optionText="name"
                      fullWidth
                      size="small"
                      disabled={identity?.role !== 'SuperAdmin'}
                      defaultValue={identity?.hotelId}
                      validate={[required()]}
                    />
                  </ReferenceInput>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormDataConsumer>
                    {({ formData }) => (
                      <ReferenceInput
                        source="zoneId"
                        reference="zones"
                        label={translate('resources.lockers.fields.zone')}
                        sort={{ field: 'name', order: 'ASC' }}
                        filter={{ hotelId: formData.hotelId || identity?.hotelId }}
                        key={formData.hotelId}
                      >
                        <SelectInput
                          optionText="name"
                          fullWidth
                          size="small"
                          validate={[required()]}
                          disabled={!formData.hotelId && !identity?.hotelId}
                        />
                      </ReferenceInput>
                    )}
                  </FormDataConsumer>
                </Grid>
              </Grid>

              {/* Tipo de Taquilla y Controlador */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormDataConsumer>
                    {({ formData }) => (
                      <ReferenceInput
                        source="lockerTypeId"
                        reference="locker-types"
                        label={translate('resources.lockers.fields.lockerType')}
                        sort={{ field: 'name', order: 'ASC' }}
                        filter={{ hotelId: formData.hotelId || identity?.hotelId }}
                      >
                        <SelectInput
                          optionText="name"
                          fullWidth
                          size="small"
                          validate={[required()]}
                          disabled={!formData.hotelId && !identity?.hotelId}
                        />
                      </ReferenceInput>
                    )}
                  </FormDataConsumer>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormDataConsumer>
                    {({ formData }) => (
                      <ReferenceInput
                        source="lockerControllerId"
                        reference="locker-controllers"
                        label={translate('resources.lockers.fields.lockerController')}
                        sort={{ field: 'name', order: 'ASC' }}
                        filter={{ hotelId: formData.hotelId || identity?.hotelId }}
                      >
                        <SelectInput
                          optionText="name"
                          fullWidth
                          size="small"
                          emptyText={translate('ra.action.none')}
                          disabled={!formData.hotelId && !identity?.hotelId}
                        />
                      </ReferenceInput>
                    )}
                  </FormDataConsumer>
                </Grid>
              </Grid>

              {/* Notas */}
              <TextInput
                source="notes"
                label={translate('resources.lockers.fields.notes')}
                multiline
                rows={3}
                fullWidth
                size="small"
              />
            </MyFormSection>
          </CenteredFormLayout>
        </SimpleForm>
      </MyFormContainer>
    </Create>
  );
};

export default LockerCreate;